import React, { useState } from 'react';
import { Button } from './ui/button';
import { ArrowRight, Calendar, TrendingUp, BarChart3 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import BookingModal from './BookingModal';

const HERO_IMAGE = 'https://customer-assets.emergentagent.com/job_619b4d5e-f51b-467f-ae93-a95fb1e339d8/artifacts/iqegr389_IllustrHeroSec.png';
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_mall-marketing/artifacts/4o6owglt_BE-transparent.png';

const HeroSection = () => {
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const { t } = useLanguage();

  return (
    <section className="relative pt-24 pb-12 bg-white overflow-hidden">
      {/* Blueprint grid background */}
      <div className="absolute inset-0 blueprint-grid opacity-30" />
      
      {/* Flow lines decoration */}
      <div className="flow-lines" />

      <div className="relative max-w-[1200px] mx-auto px-6 lg:px-8">
        {/* Top Row: Badge */}
        <div className="mb-8">
          <p className="text-xl md:text-2xl font-bold text-[#1a237e]">
            {t('hero.badge')}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 items-start gap-10">
          {/* Left Content */}
          <div className="space-y-8">
            {/* Headline */}
            <h1 className="text-4xl md:text-5xl lg:text-[3.5rem] font-extrabold leading-[1.45] md:leading-[1.35] lg:leading-[1.25] tracking-tight text-[#0d1442]">
              {t('hero.headline')}{' '}
              <span className="gradient-text">{t('hero.headlineHighlight')}</span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed max-w-xl">
              {t('hero.subheadline')}
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={() => setIsBookingOpen(true)}
                className="btn-gradient text-white px-8 py-4 rounded-lg text-base font-semibold h-auto"
              >
                {t('hero.ctaPrimary')}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                variant="outline"
                className="px-8 py-4 rounded-lg text-base font-semibold border-2 border-[#1a237e]/20 text-[#1a237e] hover:bg-[#1a237e]/5 h-auto"
                onClick={() => document.getElementById('case-studies')?.scrollIntoView({ behavior: 'smooth' })}
              >
                {t('hero.ctaSecondary')}
              </Button>
            </div>
          </div>

          {/* Right Visual */}
          <div className="relative hidden lg:block">
            {/* Main Image */}
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <div className="image-overlay absolute inset-0 z-10" />
              <img 
                src={HERO_IMAGE}
                alt="Modern shopping centre interior"
                className="w-full h-[500px] object-cover"
              />
            </div>

            {/* Floating Card 1 - Campaign Calendar */}
            <div className="floating-card absolute -left-8 top-16 p-4 rounded-2xl shadow-xl max-w-[200px]">
              <div className="flex items-center gap-2 mb-3">
                <div className="icon-badge p-2 rounded-xl">
                  <Calendar className="w-4 h-4 text-[#1a237e]" />
                </div>
                <span className="text-sm font-semibold text-[#0d1442]">{t('hero.campaignCalendar')}</span>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#5c6bc0]" />
                  <span className="text-xs text-slate-600">{t('hero.winterSales')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#4dd0e1]" />
                  <span className="text-xs text-slate-600">{t('hero.valentinesDay')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#1a237e]" />
                  <span className="text-xs text-slate-600">{t('hero.loyaltyWeek')}</span>
                </div>
              </div>
            </div>

            {/* Floating Card 2 - Monthly KPIs */}
            <div className="floating-card absolute -right-4 bottom-20 p-4 rounded-2xl shadow-xl max-w-[220px]">
              <div className="flex items-center gap-2 mb-3">
                <div className="icon-badge p-2 rounded-xl">
                  <BarChart3 className="w-4 h-4 text-[#1a237e]" />
                </div>
                <span className="text-sm font-semibold text-[#0d1442]">{t('hero.monthlyKPIs')}</span>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-600">{t('hero.footfall')}</span>
                  <div className="flex items-center gap-1">
                    <TrendingUp className="w-3 h-3 text-emerald-500" />
                    <span className="text-xs font-semibold text-emerald-600">+12%</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-600">{t('hero.engagement')}</span>
                  <div className="flex items-center gap-1">
                    <TrendingUp className="w-3 h-3 text-emerald-500" />
                    <span className="text-xs font-semibold text-emerald-600">+8%</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-600">Conversions</span>
                  <div className="flex items-center gap-1">
                    <TrendingUp className="w-3 h-3 text-emerald-500" />
                    <span className="text-xs font-semibold text-emerald-600">+15%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Booking Modal */}
      <BookingModal 
        isOpen={isBookingOpen} 
        onClose={() => setIsBookingOpen(false)} 
      />
    </section>
  );
};

export default HeroSection;
